import {
  getLocalisationVars,
  getMonthData,
  getPriceProviderData,
} from '../../selectors';
import * as analyticsConstants from '../constants/analyticsConstants';
import { getTotalPrice } from '../data-parsing/gridEntryReader';

import type {
  MonthDataGridElement,
  MonthGridData,
  PriceProviderData,
  State,
} from '../../selectors/types';

const getPriceAccuracyRatio = (
  previousPrice: number,
  currentPrice: number | null,
  logType: string,
) => {
  if (!currentPrice) {
    if (logType === analyticsConstants.MINI_EVENT) return 0;
    return analyticsConstants.MP_PROPERTY_NO_PRICE;
  }

  const normalisedCurrent = Math.ceil(currentPrice);

  if (normalisedCurrent === previousPrice) return 0;
  if (previousPrice < normalisedCurrent) {
    return Math.ceil(
      ((normalisedCurrent - previousPrice) * 100) / previousPrice,
    );
  }
  return Math.floor(
    ((normalisedCurrent - previousPrice) * 100) / previousPrice,
  );
};

const getCheapestEntry = (gridEntries: MonthDataGridElement[]) =>
  gridEntries.reduce((prev, current) => {
    const prevPrice = getTotalPrice(prev) || Infinity;
    const currentPrice = getTotalPrice(current) || Infinity;
    if (prevPrice < currentPrice) return prev;
    return current;
  });

const priceAccuracyTracker = (
  dataObject: Record<string, any>,
  state: State,
  firstVisiblePrice: number | null,
  isInitialLoad: boolean,
  logType: string,
) => {
  const providerData: PriceProviderData = getPriceProviderData(state);
  if (!providerData) return dataObject;
  const localisation = getLocalisationVars(state);
  if (localisation.currency !== providerData.previousCurrency)
    return dataObject;

  const { previousPrice } = providerData;
  const gridData: MonthGridData = getMonthData(state);
  const gridEntries = gridData.reduce((acc, cur) => acc.concat(cur), []);
  const cheapestEntry = getCheapestEntry(gridEntries);
  const cheapestPrice: number | null = getTotalPrice(cheapestEntry);
  const firstVisiblePriceRatio = getPriceAccuracyRatio(
    previousPrice,
    firstVisiblePrice,
    logType,
  );
  const cheapestPriceRatio = getPriceAccuracyRatio(
    previousPrice,
    cheapestPrice,
    logType,
  );

  let props: Record<string, any> = {
    [analyticsConstants.MP_PROPERTY_FIRST_VISIBLE_PRICE_RATIO]:
      firstVisiblePriceRatio,
    [analyticsConstants.MP_PROPERTY_CHEAPEST_PRICE_RATIO]: cheapestPriceRatio,
    [analyticsConstants.MP_PROPERTY_PROMOTED_PRICE_PROVIDER]:
      providerData.previousProvider,
    [analyticsConstants.MP_PROPERTY_INITIAL_LOAD]: isInitialLoad,
  };

  if (logType === analyticsConstants.MINI_EVENT) {
    props = {
      [analyticsConstants.ME_PROPERTY_INITIAL_LOAD]: isInitialLoad,
      [analyticsConstants.ME_PROPERTY_FIRST_VISIBLE_PRICE_DIFF_PERCENT]:
        firstVisiblePriceRatio,
      [analyticsConstants.ME_PROPERTY_CHEAPEST_PRICE_DIFF_PERCENT]:
        cheapestPriceRatio,
      [analyticsConstants.ME_PROPERTY_PROMOTED_PRICE_PROVIDER]:
        providerData.previousProvider,
    };
  }
  return { ...dataObject, ...props };
};

export default priceAccuracyTracker;
